<template>
    <div class="rs-source">
        <router-view></router-view>

        <div class="rs-page">
            <!-- 标题横幅 -->
            <div class="rs-banner">
                <h1>教研资源列表</h1>
            </div>

            <!-- KPI -->
            <div class="rs-kpis">
                <div class="rs-kpi" style="--c:#4fc3ff;">
                    <div class="rs-ring">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4fc3ff" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                    </div>
                    <div class="info">
                        <div class="label">资源总数</div>
                        <div class="value">{{ stat.total }}<small>条</small></div>
                        <div class="trend">已设置类型 {{ stat.typed }} 条 · 未设置 {{ stat.unset }} 条</div>
                    </div>
                </div>

                <div class="rs-kpi" style="--c:#35e0b0;">
                    <div class="rs-ring">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#35e0b0" stroke-width="2"><path d="M3 21h18M6 21V8l6-5 6 5v13"/><path d="M10 21v-5h4v5"/></svg>
                    </div>
                    <div class="info">
                        <div class="label">覆盖单位</div>
                        <div class="value">{{ stat.units }}<small>家</small></div>
                        <div class="trend">{{ stat.topDept ? stat.topDept.name + ' ' + stat.topDept.value + ' 条最多' : '暂无数据' }}</div>
                    </div>
                </div>

                <div class="rs-kpi" style="--c:#a78bfa;">
                    <div class="rs-ring">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" stroke-width="2"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>
                    </div>
                    <div class="info">
                        <div class="label">文档资源</div>
                        <div class="value">{{ stat.doc }}<small>份</small></div>
                        <div class="trend">{{ stat.docText }}</div>
                    </div>
                </div>

                <div class="rs-kpi" style="--c:#ffd24a;">
                    <div class="rs-ring">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffd24a" stroke-width="2"><rect x="2" y="5" width="14" height="14" rx="2"/><path d="M16 10l6-3v10l-6-3z"/></svg>
                    </div>
                    <div class="info">
                        <div class="label">视频资源</div>
                        <div class="value">{{ stat.video }}<small>个</small></div>
                        <div class="trend">另有 <i>{{ stat.unset }}</i> 条资源未设置类型</div>
                    </div>
                </div>
            </div>

            <!-- 中段三栏 -->
            <div class="rs-grid">
                <div class="rs-card">
                    <div class="rs-title">资源类型构成<span class="more">共 {{ stat.total }} 条</span></div>
                    <div ref="typeChart" class="rs-chart"></div>
                </div>

                <div class="rs-card">
                    <div class="rs-title" style="--accent:#35e0b0;">单位资源数<span class="more">全部 {{ stat.units }} 家</span></div>
                    <div class="rs-rank">
                        <div class="rank-row" v-for="(r, i) in stat.deptRank" :key="r.name">
                            <div class="rank-no" :class="'n' + (i + 1)">{{ i + 1 }}</div>
                            <div class="rank-name" :title="r.name">{{ r.name }}</div>
                            <div class="rank-track">
                                <div class="rank-fill" :class="fillCls(i)" :style="{ width: r.pct + '%' }"></div>
                            </div>
                            <div class="rank-val"><b>{{ r.value }}</b> 条</div>
                        </div>
                        <div v-if="!stat.deptRank.length" class="rs-empty">暂无数据</div>
                    </div>
                </div>

                <div class="rs-card">
                    <div class="rs-title" style="--accent:#ffd24a;">新增趋势<span class="more">单位：条</span></div>
                    <div ref="trendChart" class="rs-chart"></div>
                </div>
            </div>

            <!-- 明细表 -->
            <div class="rs-card">
                <div class="rs-title">资源明细<span class="more">共 {{ total }} 条</span></div>

                <div class="rs-toolbar">
                    <div class="search-box">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4f77ab" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
                        <input type="text" v-model="query.name" placeholder="资源名称" @keyup.enter="doSearch" />
                    </div>
                    <select v-model="query.type">
                        <option value="">全部类型</option>
                        <option value="1">文本</option>
                        <option value="2">超链接</option>
                        <option value="3">Word</option>
                        <option value="4">Excel</option>
                        <option value="5">PPT</option>
                        <option value="6">图片</option>
                        <option value="7">视频</option>
                        <option value="0">未设置类型</option>
                    </select>
                    <select v-model="query.deptId">
                        <option value="">全部单位</option>
                        <option v-for="d in stat.deptOptions" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                    <button class="btn btn-primary" @click="doSearch">查询</button>
                    <button class="btn" @click="resetSearch">重置</button>
                    <button class="btn btn-add" @click="$router.push({ name: 'source_add' })">+ 上传资源</button>
                </div>

                <table>
                    <colgroup>
                        <col style="width:238px"><col style="width:92px"><col style="width:300px">
                        <col style="width:96px"><col style="width:132px"><col style="width:86px">
                        <col style="width:132px"><col style="width:150px">
                    </colgroup>
                    <thead>
                        <tr>
                            <th>资源名称</th><th>类型</th><th>资源描述</th><th>内容形态</th>
                            <th>所属单位</th><th>知识点</th><th>创建时间</th><th>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in tableData" :key="row.id">
                            <td class="t-name" :title="row.name" @click="handlePreview(row)">{{ row.name || '-' }}</td>
                            <td>
                                <span class="tag" :class="typeCls(row.type)">{{ row.type ? getTypeName(row.type) : '未设置' }}</span>
                            </td>
                            <td class="t-desc" :title="row.description">{{ row.description || '-' }}</td>
                            <td>
                                <span class="tag-file" :class="{ none: !row.resourceUri && row.type !== 1 }"
                                      :title="row.type === 1 ? '文本内容' : (row.resourceUri || '')">
                                    {{ fileTag(row) }}
                                </span>
                            </td>
                            <td class="t-dept" :title="row.departmentName">{{ row.departmentName || '—' }}</td>
                            <td>
                                <span class="kp-badge" :class="{ zero: !kpCount[row.id] }"
                                      :title="kpCount[row.id] ? '查看该资源的 ' + kpCount[row.id] + ' 个知识点' : '暂无知识点'"
                                      @click="goKeypoint(row)">{{ kpCount[row.id] || 0 }}</span>
                            </td>
                            <td class="t-time">{{ formatTime(row.createTime) }}</td>
                            <td class="op">
                                <span @click="handlePreview(row)">预览</span>
                                <span @click="$router.push({ name: 'source_edit', query: { id: row.id } })">编辑</span>
                                <el-popconfirm
                                    title="是否确定删除？"
                                    popper-class="rs-dark-popper"
                                    @confirm="handleDel(row)"
                                >
                                    <span slot="reference" class="del">删除</span>
                                </el-popconfirm>
                            </td>
                        </tr>
                        <tr v-if="!loading && !tableData.length">
                            <td colspan="8" class="rs-empty">暂无资源数据</td>
                        </tr>
                    </tbody>
                </table>

                <div class="table-foot">
                    <span>共 {{ total }} 条 · 每页 {{ query.size }} 条 · 当前第 {{ query.page }} 页</span>
                    <el-pagination
                        background
                        layout="prev, pager, next, sizes"
                        :total="total"
                        :page-size="query.size"
                        :page-sizes="pageSizes"
                        :current-page="query.page"
                        @current-change="handlePageChange"
                        @size-change="handleSizeChange"
                    >
                    </el-pagination>
                </div>
            </div>
        </div>

        <!-- 文本预览对话框 -->
        <el-dialog
            title="文本预览"
            :visible.sync="textPreviewVisible"
            width="600px"
            custom-class="rs-dark-dialog">
            <div class="text-preview-content">{{ previewContent }}</div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="textPreviewVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <!-- 图片预览 -->
        <el-dialog
            title="图片预览"
            :visible.sync="imagePreviewVisible"
            width="800px"
            custom-class="rs-dark-dialog">
            <div class="image-preview-content">
                <img :src="previewUrl" style="max-width: 100%; max-height: 500px;" />
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="imagePreviewVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <!-- 视频预览 -->
        <el-dialog
            title="视频预览"
            :visible.sync="videoPreviewVisible"
            width="800px"
            custom-class="rs-dark-dialog"
            @close="handleVideoClose">
            <div class="video-preview-content">
                <video
                    ref="previewVideo"
                    :src="previewUrl"
                    controls
                    preload="auto"
                    controlslist="nodownload"
                    oncontextmenu="return false"
                    style="max-width: 100%; max-height: 500px;">
                </video>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="videoPreviewVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <!-- 文档预览 -->
        <el-dialog
            title="文档预览"
            :visible.sync="docPreviewVisible"
            width="90%"
            top="5vh"
            custom-class="doc-preview-dialog rs-dark-dialog"
            @close="handleDocPreviewClose">
            <div v-loading="docPreviewLoading" element-loading-text="文档加载中..." element-loading-background="rgba(6,21,48,0.72)" style="height: 75vh;">
                <iframe
                    v-if="previewUrl"
                    ref="docPreviewIframe"
                    :src="previewUrl"
                    style="width: 100%; height: 100%; border: none;"
                    allowfullscreen
                    @load="handleIframeLoad">
                </iframe>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import * as echarts from 'echarts'
import { lists, del, getPreviewUrl } from '@/request/api/source.js'
import { lists as keypointLists } from '@/request/api/keypoint.js'
import moment from 'moment'
export default {
    data() {
        return {
            tableData: [],
            // type/deptId 为空串时不提交，交由后端按角色判定数据范围
            query: { name: '', type: '', deptId: '', page: 1, size: 10 },
            pageSizes: [10, 30, 50, 100],
            total: 0,
            loading: false,
            // 全量资源（用于 KPI / 图表统计）
            allRows: [],
            // resourceId -> 知识点数量
            kpCount: {},
            stat: {
                total: 0,
                typed: 0,
                unset: 0,
                units: 0,
                doc: 0,
                video: 0,
                docText: '',
                topDept: null,
                deptRank: [],
                deptOptions: [],
                typeDist: [],
                trend: { months: [], values: [] }
            },
            // 资源类型映射
            typeMap: {
                1: '文本',
                2: '超链接',
                3: 'Word',
                4: 'Excel',
                5: 'PPT',
                6: '图片',
                7: '视频'
            },
            typeColor: {
                1: '#35e0b0',
                2: '#ffd24a',
                3: '#4fc3ff',
                4: '#4fc3ff',
                5: '#ff8f7c',
                6: '#35e0b0',
                7: '#a78bfa'
            },
            // 预览相关
            textPreviewVisible: false,
            imagePreviewVisible: false,
            videoPreviewVisible: false,
            docPreviewVisible: false,
            previewContent: '',
            previewUrl: '',
            docPreviewLoading: false,
            // IMM 预览凭证
            immAccessToken: '',
            immRefreshToken: ''
        }
    },
    watch: {
        $route(val, old) {
            // 返回刷新
            if (val.name == 'source') {
                this.initStats()
                this.init()
            }
        }
    },
    created() {
        this.initStats()
        this.init()
    },
    mounted() {
        window.addEventListener('resize', this.onResize)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
        if (this.typeChart) this.typeChart.dispose()
        if (this.trendChart) this.trendChart.dispose()
    },
    methods: {
        // 表格分页数据
        init() {
            this.loading = true
            lists(this.buildParams()).then(res => {
                this.tableData = res.data.list || []
                this.total = res.data.total || 0
            }).finally(() => {
                this.loading = false
            })
        },
        // 查询参数：空串不提交（口径由后端按角色判定，超管看全部）
        buildParams() {
            const params = { page: this.query.page, size: this.query.size }
            if (this.query.name) params.name = this.query.name
            if (this.query.type !== '') params.type = this.query.type
            if (this.query.deptId !== '') params.deptId = this.query.deptId
            return params
        },
        // 全量数据 → KPI + 图表
        initStats() {
            Promise.all([
                lists({ page: 1, size: 1000 }),
                keypointLists({ page: 1, size: 1000 })
            ]).then(([res, kpRes]) => {
                this.allRows = res.data.list || []
                const kps = kpRes.data.list || []
                const kpMap = {}
                kps.forEach(k => {
                    if (k.resourceId != null) kpMap[k.resourceId] = (kpMap[k.resourceId] || 0) + 1
                })
                this.kpCount = kpMap
                this.buildStat()
                this.$nextTick(() => this.renderCharts())
            }).catch(() => {})
        },
        buildStat() {
            const rows = this.allRows
            const deptMap = {}
            const typeMap = {}
            const monthMap = {}
            let doc = 0, video = 0, unset = 0
            const docDetail = {}

            rows.forEach(r => {
                // 单位分布
                const dn = r.departmentName || '未归属'
                if (!deptMap[dn]) deptMap[dn] = { id: r.deptId, name: dn, value: 0 }
                deptMap[dn].value++

                // 类型分布
                const tn = r.type ? this.getTypeName(r.type) : '未设置'
                typeMap[tn] = (typeMap[tn] || 0) + 1

                if (!r.type) unset++
                if (r.type === 7) video++
                if (r.type === 3 || r.type === 4 || r.type === 5) {
                    doc++
                    docDetail[this.getTypeName(r.type)] = (docDetail[this.getTypeName(r.type)] || 0) + 1
                }

                // 新增趋势（按月）
                if (r.createTime) {
                    const m = moment(r.createTime).format('YY-MM')
                    monthMap[m] = (monthMap[m] || 0) + 1
                }
            })

            const deptRank = Object.values(deptMap).sort((a, b) => b.value - a.value)
            const max = deptRank.length ? deptRank[0].value : 1
            deptRank.forEach(d => { d.pct = Math.max(Math.round(d.value / max * 100), 6) })

            const months = Object.keys(monthMap).sort()
            const docText = Object.keys(docDetail).length
                ? Object.keys(docDetail).map(k => k + ' ' + docDetail[k]).join(' · ')
                : '暂无文档资源'

            this.stat = {
                total: rows.length,
                typed: rows.length - unset,
                unset: unset,
                units: deptRank.length,
                doc: doc,
                video: video,
                docText: docText,
                topDept: deptRank.length ? deptRank[0] : null,
                deptRank: deptRank,
                deptOptions: deptRank.map(d => ({ id: d.id, name: d.name })),
                typeDist: Object.keys(typeMap).map(k => ({ name: k, value: typeMap[k] })),
                trend: {
                    months: months,
                    values: months.map(m => monthMap[m])
                }
            }
        },
        onResize() {
            if (this.typeChart) this.typeChart.resize()
            if (this.trendChart) this.trendChart.resize()
        },
        // 资源类型构成环形图
        renderCharts() {
            const typeEl = this.$refs.typeChart
            if (typeEl) {
                this.typeChart = echarts.init(typeEl)
                const dist = this.stat.typeDist
                const total = this.stat.total
                const colorOf = name => {
                    if (name === '未设置') return '#5f7ba6'
                    const hit = Object.keys(this.typeMap).find(k => this.typeMap[k] === name)
                    return this.typeColor[hit] || '#4fc3ff'
                }
                this.typeChart.setOption({
                    tooltip: {
                        trigger: 'item',
                        confine: true,
                        backgroundColor: 'rgba(6,21,48,.95)',
                        borderColor: 'rgba(63,156,255,.5)',
                        textStyle: { color: '#cfe6ff', fontSize: 12 },
                        formatter: p => p.name + '：' + p.value + ' 条（' + p.percent + '%）'
                    },
                    legend: {
                        orient: 'vertical',
                        right: 6,
                        top: 'middle',
                        itemWidth: 8,
                        itemHeight: 8,
                        itemGap: 10,
                        icon: 'rect',
                        formatter: name => {
                            const hit = dist.find(d => d.name === name)
                            return name + '  ' + (hit ? hit.value : 0)
                        },
                        textStyle: { color: '#a9c6e8', fontSize: 11 }
                    },
                    color: dist.map(d => colorOf(d.name)),
                    series: [{
                        type: 'pie',
                        radius: ['54%', '76%'],
                        center: ['32%', '52%'],
                        avoidLabelOverlap: true,
                        itemStyle: { borderColor: '#061530', borderWidth: 2 },
                        label: { show: false },
                        labelLine: { show: false },
                        data: dist.map(d => ({ name: d.name, value: d.value }))
                    }],
                    graphic: total ? [
                        {
                            type: 'text', left: '26%', top: '44%',
                            style: { text: String(total), fill: '#eaf6ff', font: '500 20px "DIN Alternate", sans-serif', textAlign: 'center' }
                        },
                        {
                            type: 'text', left: '27%', top: '60%',
                            style: { text: '条资源', fill: '#7ea6d9', font: '11px sans-serif', textAlign: 'center' }
                        }
                    ] : []
                })
            }

            const trendEl = this.$refs.trendChart
            if (trendEl) {
                this.trendChart = echarts.init(trendEl)
                const t = this.stat.trend
                const maxV = t.values.length ? Math.max.apply(null, t.values) : 0
                this.trendChart.setOption({
                    tooltip: {
                        trigger: 'axis',
                        confine: true,
                        backgroundColor: 'rgba(6,21,48,.95)',
                        borderColor: 'rgba(63,156,255,.5)',
                        textStyle: { color: '#cfe6ff', fontSize: 12 },
                        axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(79,195,255,.12)' } },
                        formatter: p => p[0].axisValue + ' 新增 ' + p[0].value + ' 条'
                    },
                    grid: { left: 6, right: 8, top: 14, bottom: 2, containLabel: true },
                    xAxis: {
                        type: 'category',
                        data: t.months,
                        axisLine: { lineStyle: { color: 'rgba(63,156,255,.35)' } },
                        axisTick: { show: false },
                        axisLabel: { color: '#7ea6d9', fontSize: 10 }
                    },
                    yAxis: {
                        type: 'value',
                        minInterval: 1,
                        max: maxV ? undefined : 1,
                        splitLine: { lineStyle: { color: 'rgba(63,156,255,.12)' } },
                        axisLabel: { color: '#7ea6d9', fontSize: 10 }
                    },
                    series: [{
                        type: 'bar',
                        data: t.values,
                        barMaxWidth: 26,
                        itemStyle: {
                            borderRadius: [3, 3, 0, 0],
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: 'rgba(79,195,255,.85)' },
                                { offset: 1, color: 'rgba(27,98,224,.35)' }
                            ]),
                            borderColor: '#4fc3ff',
                            borderWidth: 1
                        }
                    }]
                })
            }
        },
        fillCls(i) {
            return ['gold', 'green', 'purple', '', ''][i] || ''
        },
        doSearch() {
            this.query.page = 1
            this.init()
        },
        resetSearch() {
            this.query.name = ''
            this.query.type = ''
            this.query.deptId = ''
            this.query.page = 1
            this.init()
        },
        // 分页切换
        handlePageChange(page) {
            this.query.page = page
            this.init()
        },
        // 每页条数切换
        handleSizeChange(size) {
            this.query.size = size
            this.query.page = 1
            this.init()
        },
        // 跳转到资源知识点页并带上 resourceId
        goKeypoint(row) {
            if (!this.kpCount[row.id]) return
            this.$router.push({ name: 'keypoint', query: { resourceId: row.id } }).catch(function () {})
        },
        // 删除
        handleDel(data) {
            del({ ids: [data.id] }).then(res => {
                if (res.code != 0) return this.$notify.error({ title: '错误', message: res.msg });
                this.initStats()
                this.init()
                this.$notify({ title: '成功', message: res.msg, type: 'success' });
            })
        },
        // 获取资源类型名称
        getTypeName(type) {
            return this.typeMap[type] || '-'
        },
        typeCls(type) {
            const cls = { 1: 'tag-text', 2: 'tag-link', 3: 'tag-doc', 4: 'tag-doc', 5: 'tag-doc', 6: 'tag-text', 7: 'tag-video' }
            return cls[type] || 'tag-none'
        },
        // 内容形态徽标：由文件后缀推导
        fileTag(row) {
            if (row.type === 1) return '文本'
            if (!row.resourceUri) return '无文件'
            if (row.type === 2) return '链接'
            const clean = String(row.resourceUri).split('?')[0].split('#')[0]
            const m = clean.match(/\.([a-z0-9]+)$/i)
            if (m) return m[1].toUpperCase()
            return row.type ? this.getTypeName(row.type) : '文件'
        },
        // 格式化时间
        formatTime(time) {
            if (!time) return '-'
            return moment(time).format('YYYY-MM-DD HH:mm')
        },
        // 预览
        handlePreview(row) {
            const type = row.type
            const apiBaseURL = window.apiUrl?.baseURL || process.env.VUE_APP_BASE_URL || ''

            switch (type) {
                case 1: // 文本
                    this.previewContent = row.content || '暂无内容'
                    this.textPreviewVisible = true
                    break
                case 2: // 超链接
                    if (row.resourceUri) {
                        window.open(row.resourceUri, '_blank')
                    } else {
                        this.$message.warning('暂无链接地址')
                    }
                    break
                case 3: // Word
                case 4: // Excel
                case 5: // PPT
                    if (row.resourceUri) {
                        // 使用阿里云 IMM 预览文档
                        const resourceUrl = row.resourceUri.startsWith('http') ? row.resourceUri : (apiBaseURL + row.resourceUri)
                        this.docPreviewVisible = true
                        this.docPreviewLoading = true
                        this.previewUrl = ''
                        this.immAccessToken = ''
                        this.immRefreshToken = ''

                        getPreviewUrl(resourceUrl).then(data => {
                            const { previewUrl, accessToken, refreshToken } = data

                            if (!previewUrl) {
                                this.$message.error('预览地址为空')
                                this.docPreviewVisible = false
                                return
                            }

                            // 保存 token 用于 postMessage
                            this.immAccessToken = accessToken
                            this.immRefreshToken = refreshToken

                            // 将 accessToken 添加到 URL 参数
                            let finalUrl = previewUrl
                            if (accessToken) {
                                const separator = previewUrl.includes('?') ? '&' : '?'
                                finalUrl = `${previewUrl}${separator}accessToken=${accessToken}`
                            }

                            this.previewUrl = finalUrl
                        }).catch(error => {
                            // 更详细的错误提示
                            let errorMsg = '获取预览凭证失败'
                            if (error.message) {
                                errorMsg = error.message
                            } else if (typeof error === 'string') {
                                errorMsg = error
                            }
                            this.$message.error(errorMsg)
                            this.docPreviewVisible = false
                        }).finally(() => {
                            this.docPreviewLoading = false
                        })
                    } else {
                        this.$message.warning('暂无文件地址')
                    }
                    break
                case 6: // 图片
                    if (row.resourceUri) {
                        this.previewUrl = apiBaseURL + row.resourceUri
                        this.imagePreviewVisible = true
                    } else {
                        this.$message.warning('暂无图片地址')
                    }
                    break
                case 7: // 视频
                    if (row.resourceUri) {
                        const videoUrl = row.resourceUri.startsWith('http') ? row.resourceUri : (apiBaseURL + row.resourceUri)
                        this.previewUrl = ''
                        this.videoPreviewVisible = true
                        // 延迟1秒后设置URL并自动播放
                        setTimeout(() => {
                            this.previewUrl = videoUrl
                            // 等待DOM更新后播放
                            this.$nextTick(() => {
                                if (this.$refs.previewVideo) {
                                    this.$refs.previewVideo.play().catch(err => {
                                        console.log('自动播放失败:', err)
                                    })
                                }
                            })
                        }, 1000)
                    } else {
                        this.$message.warning('暂无视频地址')
                    }
                    break
                default:
                    this.$message.warning('未知资源类型')
            }
        },
        // 视频对话框关闭时暂停视频
        handleVideoClose() {
            if (this.$refs.previewVideo) {
                this.$refs.previewVideo.pause()
            }
        },
        // 文档预览对话框关闭
        handleDocPreviewClose() {
            this.previewUrl = ''
            this.immAccessToken = ''
            this.immRefreshToken = ''
            // 移除消息监听
            window.removeEventListener('message', this.handleImmMessage)
        },
        // 监听 IMM iframe 消息
        handleImmMessage(event) {
            // 监听 IMM 请求 token 的消息
            if (event.data && (event.data.type === 'getAccessToken' || event.data === 'getAccessToken')) {
                if (this.$refs.docPreviewIframe && this.immAccessToken) {
                    this.$refs.docPreviewIframe.contentWindow.postMessage({
                        type: 'setAccessToken',
                        data: {
                            accessToken: this.immAccessToken,
                            refreshToken: this.immRefreshToken
                        }
                    }, '*')
                }
            }
        },
        // iframe 加载完成后传递 token
        handleIframeLoad() {
            // 注册消息监听
            window.addEventListener('message', this.handleImmMessage)

            // 直接发送 token（某些情况下 IMM 会主动接收）
            if (this.$refs.docPreviewIframe && this.immAccessToken) {
                setTimeout(() => {
                    this.$refs.docPreviewIframe.contentWindow.postMessage({
                        type: 'setAccessToken',
                        data: {
                            accessToken: this.immAccessToken,
                            refreshToken: this.immRefreshToken
                        }
                    }, '*')
                }, 100)
            }
        }
    }
}
</script>

<style scoped lang="less">
.rs-source {
    .rs-page {
        margin: 0 10px 20px;
        color: #cfe6ff;
        font-size: 13px;
    }

    /* 标题横幅 */
    .rs-banner {
        position: relative;
        text-align: center;
        padding: 14px 0 10px;
        margin-bottom: 16px;

        h1 {
            display: inline-block;
            font-size: 24px;
            font-weight: 500;
            letter-spacing: 6px;
            color: #eaf6ff;
            text-shadow: 0 0 18px rgba(64, 156, 255, 0.9), 0 0 40px rgba(32, 110, 255, 0.5);
            padding: 6px 56px;
            background: linear-gradient(180deg, rgba(24, 80, 190, 0.45), rgba(10, 35, 90, 0.15));
            clip-path: polygon(24px 0, calc(100% - 24px) 0, 100% 100%, 0 100%);
            border-bottom: 1px solid rgba(80, 180, 255, 0.7);
            margin: 0;
        }

        &::before,
        &::after {
            content: '';
            position: absolute;
            top: 26px;
            width: 180px;
            height: 2px;
            background: linear-gradient(90deg, transparent, #3f9cff);
        }

        &::before { left: 6%; }
        &::after { right: 6%; transform: scaleX(-1); }
    }

    /* KPI */
    .rs-kpis {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 14px;
        margin-bottom: 16px;
    }

    .rs-kpi {
        display: flex;
        align-items: center;
        gap: 14px;
        background: linear-gradient(180deg, rgba(16, 55, 130, 0.35), rgba(8, 25, 60, 0.35));
        border: 1px solid rgba(63, 156, 255, 0.35);
        border-radius: 8px;
        padding: 14px 18px;
        position: relative;
        overflow: hidden;

        &::after {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 3px;
            background: linear-gradient(180deg, var(--c, #4fc3ff), rgba(27, 98, 224, 0.7));
            box-shadow: 0 0 10px var(--c, #4fc3ff);
        }

        &::before {
            content: '';
            position: absolute;
            right: -30px;
            top: -30px;
            width: 90px;
            height: 90px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(63, 156, 255, 0.18), transparent 70%);
        }

        .rs-ring {
            flex: none;
            width: 52px;
            height: 52px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(30, 90, 200, 0.5), rgba(8, 25, 60, 0.9));
            border: 1px solid rgba(79, 195, 255, 0.55);
            box-shadow: 0 0 14px rgba(63, 156, 255, 0.4), inset 0 0 12px rgba(63, 156, 255, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .info .label {
            font-size: 12px;
            color: #7ea6d9;
            margin-bottom: 4px;
        }

        .info .value {
            font-size: 26px;
            font-weight: 500;
            font-family: 'DIN Alternate', 'Segoe UI', sans-serif;
            background: linear-gradient(180deg, #ffffff, var(--c, #4fc3ff));
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;

            small {
                font-size: 12px;
                margin-left: 3px;
            }
        }

        .info .trend {
            font-size: 11px;
            margin-top: 3px;
            color: #8fb4dc;

            i {
                color: #ffd24a;
                font-style: normal;
            }
        }
    }

    /* 卡片 */
    .rs-card {
        background: linear-gradient(180deg, rgba(13, 40, 95, 0.35), rgba(5, 18, 45, 0.55));
        border: 1px solid rgba(63, 156, 255, 0.28);
        border-radius: 8px;
        padding: 14px 16px;
        box-shadow: inset 0 0 24px rgba(20, 70, 170, 0.15);
        margin-bottom: 16px;
    }

    .rs-grid {
        display: grid;
        grid-template-columns: 4fr 4fr 4fr;
        gap: 14px;
        margin-bottom: 16px;
    }

    .rs-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 500;
        color: #dceeff;
        letter-spacing: 1px;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid rgba(63, 156, 255, 0.35);

        &::before {
            content: '';
            width: 8px;
            height: 8px;
            background: var(--accent, #3f9cff);
            transform: rotate(45deg);
            box-shadow: 0 0 8px var(--accent, #3f9cff);
        }

        .more {
            margin-left: auto;
            font-size: 12px;
            color: #5f8fd0;
            font-weight: 400;
        }
    }

    .rs-chart {
        height: 208px;
        width: 100%;
    }

    /* 排行榜 */
    .rs-rank {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 2px;
    }

    .rank-row {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 12px;
    }

    .rank-no {
        flex: none;
        width: 20px;
        height: 20px;
        border-radius: 4px;
        font-size: 11px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: Consolas, monospace;
        background: rgba(63, 156, 255, 0.14);
        color: #7ea6d9;
        border: 1px solid rgba(63, 156, 255, 0.3);

        &.n1 { background: linear-gradient(180deg, #e0a300, #b8860b); color: #fff7d6; border-color: #ffd24a; box-shadow: 0 0 10px rgba(255, 210, 74, 0.55); }
        &.n2 { background: linear-gradient(180deg, #8fa6c4, #5c7392); color: #f0f6ff; border-color: #a9c6e8; }
        &.n3 { background: linear-gradient(180deg, #b9762f, #8a5220); color: #ffeede; border-color: #d99354; }
    }

    .rank-name {
        flex: none;
        width: 118px;
        color: #dceeff;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .rank-track {
        flex: 1;
        height: 8px;
        border-radius: 4px;
        background: rgba(63, 156, 255, 0.13);
        overflow: hidden;
    }

    .rank-fill {
        height: 100%;
        border-radius: 4px;
        background: linear-gradient(90deg, #1b62e0, #4fc3ff);
        box-shadow: 0 0 8px rgba(79, 195, 255, 0.65);

        &.gold { background: linear-gradient(90deg, #b8860b, #ffd24a); box-shadow: 0 0 8px rgba(255, 210, 74, 0.7); }
        &.purple { background: linear-gradient(90deg, #6b46c1, #a78bfa); box-shadow: 0 0 8px rgba(167, 139, 250, 0.7); }
        &.green { background: linear-gradient(90deg, #0f8f6a, #35e0b0); box-shadow: 0 0 8px rgba(53, 224, 176, 0.7); }
    }

    .rank-val {
        flex: none;
        width: 52px;
        text-align: right;
        font-family: Consolas, monospace;
        font-size: 13px;
        color: #6fc4ff;

        b {
            font-weight: 500;
            font-size: 14px;
        }
    }

    /* 工具栏 */
    .rs-toolbar {
        display: flex;
        gap: 10px;
        align-items: center;
        margin-bottom: 14px;
        flex-wrap: wrap;
    }

    .search-box {
        display: flex;
        align-items: center;
        gap: 8px;
        background: rgba(13, 40, 95, 0.5);
        border: 1px solid rgba(63, 156, 255, 0.4);
        border-radius: 5px;
        padding: 0 12px;
        height: 34px;
        width: 220px;

        input {
            background: transparent;
            border: none;
            outline: none;
            color: #cfe6ff;
            font-size: 13px;
            width: 100%;

            &::placeholder { color: #4f77ab; }
        }
    }

    select {
        height: 34px;
        padding: 0 10px;
        border-radius: 5px;
        background: rgba(13, 40, 95, 0.5);
        border: 1px solid rgba(63, 156, 255, 0.4);
        color: #a9c6e8;
        font-size: 13px;
        outline: none;
        cursor: pointer;

        option { background: #0a1f45; color: #a9c6e8; }
    }

    .btn {
        height: 34px;
        padding: 0 16px;
        border-radius: 5px;
        font-size: 13px;
        cursor: pointer;
        border: 1px solid rgba(63, 156, 255, 0.45);
        background: rgba(13, 40, 95, 0.5);
        color: #a9c6e8;
        transition: all 0.2s;

        &:hover { border-color: #4fc3ff; color: #4fc3ff; }
    }

    .btn-primary {
        background: linear-gradient(180deg, #2a7de8, #1b62e0);
        border: 1px solid #4fc3ff;
        color: #fff;
        box-shadow: 0 0 12px rgba(42, 125, 232, 0.6);

        &:hover { filter: brightness(1.15); color: #fff; }
    }

    .btn-add {
        margin-left: auto;
        background: linear-gradient(180deg, #22b98a, #0f8f6a);
        border: 1px solid #35e0b0;
        color: #eafff7;
        box-shadow: 0 0 12px rgba(53, 224, 176, 0.55);

        &:hover { filter: brightness(1.12); color: #fff; }
    }

    /* 表格 */
    table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
    }

    thead th {
        background: linear-gradient(180deg, rgba(30, 85, 190, 0.5), rgba(12, 38, 90, 0.5));
        color: #bfe0ff;
        font-size: 12px;
        font-weight: 500;
        padding: 12px 14px;
        text-align: left;
        letter-spacing: 1px;
        border-bottom: 1px solid rgba(79, 195, 255, 0.6);
        box-shadow: 0 2px 10px rgba(27, 98, 224, 0.25);
        white-space: nowrap;
    }

    tbody td {
        padding: 12px 14px;
        font-size: 13px;
        color: #c4dcf5;
        border-bottom: 1px solid rgba(63, 156, 255, 0.12);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    tbody tr:nth-child(even) { background: rgba(20, 60, 140, 0.12); }
    tbody tr { transition: background 0.15s; }
    tbody tr:hover { background: rgba(63, 156, 255, 0.1); }
    tbody tr:hover td:first-child { box-shadow: inset 3px 0 0 #4fc3ff; }

    .t-name {
        color: #eaf6ff;
        cursor: pointer;

        &:hover { color: #4fc3ff; text-shadow: 0 0 8px rgba(79, 195, 255, 0.6); }
    }

    .t-desc { color: #93b6de; font-size: 12px; }
    .t-dept { color: #93b6de; font-size: 12px; }
    .t-time { font-family: Consolas, monospace; font-size: 12px; color: #7ea6d9; }

    /* 类型标签 */
    .tag {
        display: inline-block;
        font-size: 12px;
        padding: 2px 10px;
        border-radius: 3px;
        white-space: nowrap;

        &.tag-doc { color: #6fc4ff; border: 1px solid rgba(79, 195, 255, 0.45); background: rgba(27, 98, 224, 0.2); }
        &.tag-video { color: #a78bfa; border: 1px solid rgba(167, 139, 250, 0.45); background: rgba(167, 139, 250, 0.12); }
        &.tag-text { color: #35e0b0; border: 1px solid rgba(53, 224, 176, 0.45); background: rgba(53, 224, 176, 0.1); }
        &.tag-link { color: #ffd24a; border: 1px solid rgba(255, 210, 74, 0.45); background: rgba(255, 210, 74, 0.1); }
        &.tag-none { color: #7ea6d9; border: 1px solid rgba(126, 166, 217, 0.35); background: rgba(126, 166, 217, 0.08); }
    }

    .tag-file {
        font-family: Consolas, monospace;
        font-size: 11px;
        color: #9ec7ff;
        border: 1px solid rgba(63, 156, 255, 0.35);
        background: rgba(27, 98, 224, 0.16);
        padding: 1px 8px;
        border-radius: 3px;

        &.none { color: #6f8db4; border-color: rgba(111, 141, 180, 0.3); background: rgba(111, 141, 180, 0.1); }
    }

    /* 知识点数徽标 */
    .kp-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 26px;
        height: 20px;
        padding: 0 7px;
        border-radius: 10px;
        font-family: Consolas, monospace;
        font-size: 12px;
        color: #35e0b0;
        background: rgba(53, 224, 176, 0.12);
        border: 1px solid rgba(53, 224, 176, 0.4);
        box-shadow: 0 0 8px rgba(53, 224, 176, 0.25);
        cursor: pointer;

        &:hover { background: rgba(53, 224, 176, 0.22); }

        &.zero {
            color: #4f77ab;
            background: rgba(63, 156, 255, 0.06);
            border-color: rgba(63, 156, 255, 0.2);
            box-shadow: none;
            cursor: default;
        }
    }

    .op {
        white-space: nowrap;

        span {
            cursor: pointer;
            font-size: 12px;
            margin-right: 10px;
            color: #6fc4ff;

            &:hover { color: #4fc3ff; text-shadow: 0 0 8px rgba(79, 195, 255, 0.6); }
        }

        .del {
            color: #ff6b6b;
            margin-right: 0;

            &:hover { color: #ff8a8a; text-shadow: 0 0 8px rgba(255, 107, 107, 0.6); }
        }
    }

    .table-foot {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 13px 0 0;
        font-size: 12px;
        color: #7ea6d9;
    }

    .rs-empty {
        padding: 24px 0;
        text-align: center;
        color: #5f8fd0;
        font-size: 12px;
    }

    ::v-deep .el-pagination {
        padding: 0;

        button,
        .el-pager li {
            background: rgba(13, 40, 95, 0.5) !important;
            color: #a9c6e8 !important;
            border: 1px solid rgba(63, 156, 255, 0.4);
            border-radius: 4px;
            min-width: 28px;
            height: 28px;
            line-height: 26px;
            font-size: 12px;
            margin: 0 3px;
        }

        button:disabled { opacity: 0.4; }

        .el-pager li.active {
            background: linear-gradient(180deg, #2a7de8, #1b62e0) !important;
            border-color: #4fc3ff;
            color: #fff !important;
            box-shadow: 0 0 10px rgba(79, 195, 255, 0.6);
        }

        .el-pager li:hover:not(.active) {
            border-color: #4fc3ff;
            color: #4fc3ff !important;
        }

        .el-pagination__sizes .el-input .el-input__inner {
            background: rgba(13, 40, 95, 0.5) !important;
            border-color: rgba(63, 156, 255, 0.4) !important;
            color: #a9c6e8 !important;
            height: 28px;
            line-height: 28px;
        }
    }

    .text-preview-content {
        max-height: 400px;
        overflow-y: auto;
        padding: 15px;
        background-color: rgba(6, 21, 48, 0.6);
        border: 1px solid rgba(63, 156, 255, 0.25);
        border-radius: 4px;
        color: #cfe6ff;
        white-space: pre-wrap;
        word-break: break-all;
        line-height: 1.6;
    }

    .image-preview-content,
    .video-preview-content {
        text-align: center;
    }

    @media (max-width: 1500px) {
        .rs-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .rs-grid { grid-template-columns: 1fr 1fr; }
    }
}
</style>

<style lang="less">
/* 弹层 / 弹窗挂在 body 下，需全局样式 */
.rs-dark-dialog {
    background: linear-gradient(180deg, rgba(10, 32, 75, 0.98), rgba(5, 18, 45, 0.98)) !important;
    border: 1px solid rgba(63, 156, 255, 0.45) !important;
    box-shadow: 0 20px 60px rgba(2, 10, 28, 0.8);

    .el-dialog__header {
        border-bottom: 1px solid rgba(63, 156, 255, 0.2);
    }

    .el-dialog__title {
        color: #eaf6ff;
        font-size: 15px;
        letter-spacing: 1px;
    }

    .el-dialog__headerbtn .el-icon-close {
        color: #7ea6d9;
    }

    .el-dialog__body {
        color: #cfe6ff;
    }

    .el-button {
        background: rgba(13, 40, 95, 0.6);
        border-color: rgba(63, 156, 255, 0.45);
        color: #a9c6e8;
    }

    .el-button:hover {
        border-color: #4fc3ff;
        color: #4fc3ff;
    }
}

.rs-dark-popper {
    background: rgba(10, 32, 75, 0.96) !important;
    border: 1px solid rgba(63, 156, 255, 0.45) !important;
    color: #cfe6ff !important;

    .el-popconfirm__main {
        color: #cfe6ff !important;
    }

    .el-popconfirm__action .el-button--text {
        color: #6fc4ff !important;
    }

    .popper__arrow::after {
        border-top-color: rgba(10, 32, 75, 0.96) !important;
    }
}
</style>
